import { waPhone, cleanPhoneInput } from "@/lib/phone";
import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import api, { API, getAuthToken, formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { JobWorkBadge, CostingSection } from "@/components/SheetBilling";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import StatusBadge, { STATUS_LABELS } from "@/components/StatusBadge";
import ItemPicker from "@/components/ItemPicker";
import { orderPickerItems } from "@/components/SheetBilling";
import FabricationPanel from "@/components/FabricationPanel";
import CNCCuttingSizesPanel from "@/components/CNCCuttingSizesPanel";
import CuttingPlatesSection from "@/components/CuttingPlatesSection";
import GateSheetSizes from "@/components/GateSheetSizes";
import GateCosting from "@/components/GateCosting";
import NewOrderForm from "@/components/NewOrderForm";
import { toast } from "sonner";
import {
  FileArrowUp, DownloadSimple, ArrowRight, CurrencyInr, Package,
  WhatsappLogo, FileText, ImageSquare, Plus, X, Eye, User, CheckCircle, Trash, TrashSimple, PencilSimple, ChatCircleText, ShareFat
} from "@phosphor-icons/react";
import { istToday } from "@/lib/istDate";

const STATUS_FLOW = ["new_order", "design_pending", "ready_to_cut", "in_cutting", "completed", "delivered"];

const STATUS_SHORT = {
  draft: "Draft",
  new_order: "New",
  design_pending: "Design",
  ready_to_cut: "Ready",
  in_cutting: "Cutting",
  completed: "Ready to Deliver",
  delivered: "Delivered",
};

const GATE_FLOW = ["new_order", "framing_making", "design_making", "design_cutting", "completed", "delivered"];
const GATE_SHORT = { new_order: "New", framing_making: "Framing", design_making: "Design", design_cutting: "Cutting", completed: "Ready", delivered: "Delivered" };

function StatusTimeline({ currentStatus, gate }) {
  const FLOW = gate ? GATE_FLOW : STATUS_FLOW;
  const SHORT = gate ? GATE_SHORT : STATUS_SHORT;
  const currentIdx = FLOW.indexOf(gate && currentStatus === "final_fitting" ? "completed" : currentStatus);
  return (
    <div className="flex items-center gap-1 overflow-x-auto pb-1" data-testid="status-timeline">
      {FLOW.map((s, i) => {
        const done = i < currentIdx;
        const active = i === currentIdx;
        const upcoming = i > currentIdx;
        return (
          <React.Fragment key={s}>
            <div
              className={
                "flex items-center gap-2 px-3 py-2 rounded-sm border shrink-0 " +
                (active
                  ? "border-orange-500 bg-orange-500/20 text-orange-300"
                  : done
                  ? "border-emerald-500/60 bg-emerald-500/10 text-emerald-400"
                  : "border-slate-700 bg-slate-800/40 text-slate-500")
              }
              data-testid={`timeline-${s}`}
            >
              <span className={
                "w-5 h-5 flex items-center justify-center rounded-sm text-[10px] font-mono-num font-bold " +
                (active ? "bg-orange-500 text-white"
                  : done ? "bg-emerald-500 text-slate-900"
                  : "bg-slate-700 text-slate-500")
              }>
                {done ? <CheckCircle size={12} weight="fill" /> : i + 1}
              </span>
              <span className="text-[10px] font-mono-num tracking-widest whitespace-nowrap">
                {SHORT[s]?.toUpperCase()}
              </span>
            </div>
            {i < FLOW.length - 1 && (
              <div className={
                "w-3 h-0.5 shrink-0 " +
                (upcoming ? "bg-slate-700" : "bg-emerald-500/60")
              } />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

export default function OrderDetailDialog({ orderId, open, onClose, onUpdated, isAdmin = true }) {
  const { user } = useAuth();
  const [order, setOrder] = useState(null);
  const [partyDue, setPartyDue] = useState(null);
  useEffect(() => {
    if (!order?.id || user?.role === "operator") return;
    api.get(`/orders/${order.id}/party-due`).then(({ data }) => setPartyDue(data?.party_balance != null ? data : null)).catch(() => setPartyDue(null));
  }, [order?.id, order?.balance, order?.total_amount]); // eslint-disable-line react-hooks/exhaustive-deps
  const prevDue = partyDue && Math.abs(partyDue.previous_due) > 0.01 ? partyDue : null;
  const inr = (v) => Math.round(v * 100) / 100;
  const dueLine = prevDue ? `\nPichla baaki: ₹${inr(prevDue.previous_due).toLocaleString("en-IN")}\n*Kul baaki: ₹${inr(prevDue.party_balance).toLocaleString("en-IN")}*` : "";
  const [modes, setModes] = useState([]);
  const [busy, setBusy] = useState(false);
  const [shareModal, setShareModal] = useState(null); // { link, imageUrl, text, phone }
  const [smsDialog, setSmsDialog] = useState(null); // { mobile, message, sending }
  const [editDiscount, setEditDiscount] = useState(null); // { id, amount, note }
  const [goLiveAt, setGoLiveAt] = useState(null);
  useEffect(() => {
    if (user?.role === "admin") api.get("/settings/costing").then(({ data }) => setGoLiveAt(data.go_live_at)).catch(() => {});
  }, [user?.role]);
  const [payAmt, setPayAmt] = useState("");
  const [payMode, setPayMode] = useState("cash");
  // Back-dating for owner: default today, editable via <input type="date">.
  const [payDate, setPayDate] = useState(() => istToday());
  const [lightbox, setLightbox] = useState(null); // { url, name }
  // Edit-Sheets mode: when true, the sheets table becomes inline-editable so the
  // admin can update sizes/qty/rate/margin per customer's change-request.
  const [editSheets, setEditSheets] = useState(false);
  const [draftItems, setDraftItems] = useState(null);
  const [savingSheets, setSavingSheets] = useState(false);
  const [materialItems, setMaterialItems] = useState([]); // items master for the sheet picker
  const [editCustomer, setEditCustomer] = useState(false); // inline dialog
  const [customBillOpen, setCustomBillOpen] = useState(false); // one-off customer bill
  const [custDraft, setCustDraft] = useState({ customer_name: "", mobile: "", address: "" });
  const [savingCust, setSavingCust] = useState(false);
  const [savingWelder, setSavingWelder] = useState(false);
  const [pickWelderOpen, setPickWelderOpen] = useState(false);
  const [welderOptions, setWelderOptions] = useState([]);
  const [welderQ, setWelderQ] = useState("");
  // Unified EDIT ORDER mode — swaps the entire dialog body for the
  // NewOrderForm (in edit mode) so admins get the same intake UX for edits.
  const [editingOrder, setEditingOrder] = useState(false);
  const previewRef = useRef(null);
  const dxfRef = useRef(null);
  const refRef = useRef(null);

  useEffect(() => {
    if (!orderId || !open) return;
    // Fresh open → always land in read-only view, never leaking a leftover
    // "editing" flag from a previous order.
    setEditingOrder(false);
    (async () => {
      try {
        const [{ data: o }, { data: m }, { data: its }] = await Promise.all([
          api.get(`/orders/${orderId}`),
          api.get("/settings/payment-modes"),
          api.get("/items").catch(() => ({ data: [] })),
        ]);
        setOrder(o);
        setModes(m);
        setMaterialItems(its || []);
        if (m?.length) setPayMode(m[0].key);
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail));
      }
    })();
  }, [orderId, open]);

  const refresh = async () => {
    const { data } = await api.get(`/orders/${orderId}`);
    setOrder(data);
    onUpdated?.(data);
  };

  const upload = async (kind, file, preOpenedWin = null) => {
    if (!file) {
      // If we pre-opened a window but got no file, close it.
      if (preOpenedWin && !preOpenedWin.closed) preOpenedWin.close();
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("kind", kind);
      fd.append("file", file);
      await api.post(`/orders/${orderId}/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`${labelFor(kind)} uploaded`);
      await refresh();
      // After a design upload: if we have a pre-opened window (from the
      // file-input change user gesture), navigate it directly to WhatsApp.
      // Browser will show its native "Open WhatsApp?" prompt — that's fine.
      // Fallback: try direct open, else show modal.
      if (kind === "design_preview") {
        const waUrl = buildDesignApprovalWaUrl();
        if (preOpenedWin && !preOpenedWin.closed && waUrl) {
          try { preOpenedWin.location.href = waUrl; } catch (_e) {
            setTimeout(() => openShareModal(), 200);
          }
        } else {
          const opened = tryOpenWhatsAppDirect();
          if (!opened) {
            setTimeout(() => openShareModal(), 200);
          }
        }
      }
    } catch (e) {
      // Close the pre-opened tab on failure so user isn't left with a blank tab
      if (preOpenedWin && !preOpenedWin.closed) {
        try { preOpenedWin.close(); } catch (_e) { /* ignore */ }
      }
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  // Build the WhatsApp URL for design approval (used by pre-opened window flow)
  const buildDesignApprovalWaUrl = () => {
    if (!order) return null;
    const reviewUrl = `${window.location.origin}/design-review/${order.order_number}`;
    const text =
      `*Shobhit Creation* — Order #${order.order_number}\n\n` +
      `Namaste ${order.customer_name} ji, apna design dekhne ke liye link pe click karein:\n\n` +
      `${reviewUrl}`;
    const mobile = String(order.mobile || "").replace(/\D/g, "");
    const phone = waPhone(mobile);
    return `https://wa.me/${phone}?text=${encodeURIComponent(text)}`;
  };

  const uploadMultiple = async (files) => {
    const arr = Array.from(files || []).filter((f) => f.type.startsWith("image/"));
    for (const f of arr) {
      await upload("reference_image", f);
    }
  };

  const deleteRefImage = async (image_id) => {
    try {
      await api.delete(`/orders/${orderId}/reference-image/${image_id}`);
      toast.success("Image removed");
      await refresh();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const changeStatus = async (status) => {
    setBusy(true);
    try {
      await api.post(`/orders/${orderId}/status`, { status });
      toast.success(`Moved to ${STATUS_LABELS[status]}`);
      await refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const addPayment = async (e) => {
    e.preventDefault();
    const n = Number(payAmt);
    if (!n || n <= 0) { toast.error("Enter a valid amount"); return; }
    setBusy(true);
    try {
      await api.post(`/orders/${orderId}/payment`, { amount: n, mode: payMode, paid_on: payDate });
      toast.success("Payment recorded");
      setPayAmt("");
      setPayDate(istToday());
      await refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const fileUrl = (kind, inline = false) => {
    const tok = getAuthToken() || "";
    const suffix = inline ? "&inline=1" : "";
    return `${API}/orders/${orderId}/download/${kind}?auth=${encodeURIComponent(tok)}${suffix}`;
  };
  const refImgUrl = (id) => {
    const tok = getAuthToken() || "";
    return `${API}/orders/${orderId}/reference-image/${id}?auth=${encodeURIComponent(tok)}`;
  };
  const download = (kind) => window.open(fileUrl(kind), "_blank");
  const preview = (kind) => window.open(fileUrl(kind, true), "_blank");

  // ---- Edit Sheets helpers -----------------------------------------------
  const startEditSheets = () => {
    // Clone current items into a mutable draft.
    setDraftItems((order?.items || []).map((it) => ({
      item_id: it.item_id || null,
      item_name: it.item_name || "",
      thickness: it.thickness || "",
      // Prefer RAW dims when editing (avoids compounding the +margin bump).
      width_in: it.width_in_raw ?? it.width_in ?? 0,
      height_in: it.height_in_raw ?? it.height_in ?? 0,
      quantity: it.quantity ?? 1,
      unit: it.unit || "sqft",
      rate_per_sqft: it.rate_per_sqft ?? 0,
      discount_amount: it.discount_amount ?? 0,
      sizing_mode: it.sizing_mode || "standard",
      margin_inches: it.margin_inches ?? (/\bSS\b|stainless/i.test(String(it.item_name || "")) ? 6 : 3),
    })));
    setEditSheets(true);
  };
  const cancelEditSheets = () => { setEditSheets(false); setDraftItems(null); };

  // ---- Edit Customer helpers ---------------------------------------------
  const startEditCustomer = () => {
    setCustDraft({
      customer_name: order?.customer_name || "",
      mobile: order?.mobile || "",
      address: order?.address || "",
    });
    setEditCustomer(true);
  };
  const saveEditCustomer = async () => {
    const name = (custDraft.customer_name || "").trim();
    const mob = (custDraft.mobile || "").trim();
    if (!name) { toast.error("Customer name required"); return; }
    if (!mob) { toast.error("Mobile number required"); return; }
    setSavingCust(true);
    try {
      const { data } = await api.patch(`/orders/${orderId}/customer`, {
        customer_name: name,
        mobile: mob,
        address: custDraft.address || "",
      });
      setOrder(data);
      onUpdated?.(data);
      setEditCustomer(false);
      toast.success("Customer details updated");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSavingCust(false);
    }
  };

  // ---- Toggle Welder ------------------------------------------------------
  const toggleWelder = async (checked) => {
    // Detach — just PATCH is_welder:false
    if (!checked) {
      setSavingWelder(true);
      try {
        const { data } = await api.patch(`/orders/${orderId}/welder`, { is_welder: false });
        setOrder(data);
        onUpdated?.(data);
        toast.success("Marked as one-time customer");
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail) || e.message);
      } finally {
        setSavingWelder(false);
      }
      return;
    }
    // Attach — open welder picker. Load welders master list.
    try {
      const { data } = await api.get("/welders");
      setWelderOptions(data || []);
    } catch (_e) {
      setWelderOptions([]);
    }
    setPickWelderOpen(true);
  };

  const attachWelder = async (welderId) => {
    setSavingWelder(true);
    try {
      const { data } = await api.patch(`/orders/${orderId}/welder`, { is_welder: true, welder_id: welderId });
      setOrder(data);
      onUpdated?.(data);
      setPickWelderOpen(false);
      toast.success(`Attached to welder ${data.welder?.name || ""}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSavingWelder(false);
    }
  };

  const attachAsNewWelder = async () => {
    const name = window.prompt("Naye welder ka naam:", order?.customer_name || "");
    if (!name) return;
    const shop = window.prompt("Shop / firm name (optional):", "") || "";
    setSavingWelder(true);
    try {
      const { data } = await api.patch(`/orders/${orderId}/welder`, {
        is_welder: true, welder_name: name, welder_shop: shop,
      });
      setOrder(data);
      onUpdated?.(data);
      setPickWelderOpen(false);
      toast.success(`Created + attached welder "${data.welder?.name}"`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSavingWelder(false);
    }
  };
  const updateDraftItem = (idx, field, val) => {
    setDraftItems((prev) => (prev || []).map((it, i) => (i === idx ? { ...it, [field]: val } : it)));
  };
  // Extract thickness from item name — handles "3mm", "2.5 mm", "16g",
  // "18 gauge" so the operator/PDF always sees the CORRECT thickness after
  // an item swap. Without this the order shows the OLD thickness ("3mm")
  // while the item name shows the new one ("2.5mm MS Sheet") — that mix
  // caused a real cutting mistake once (customer swapped 3mm → 2.5mm,
  // panel got cut on 3mm because operator only reads the big thickness).
  const parseThickness = (name = "") => {
    const s = String(name);
    let m = s.match(/(\d+(?:\.\d+)?)\s*mm/i);
    if (m) return `${m[1]}mm`;
    m = s.match(/(\d+(?:\.\d+)?)\s*(?:g|ga|gauge)\b/i);
    if (m) return `${m[1]}g`;
    return null;
  };

  const pickDraftItem = (idx, itemId) => {
    if (!itemId) {
      updateDraftItem(idx, "item_id", "");
      updateDraftItem(idx, "item_name", "");
      return;
    }
    const m = materialItems.find((x) => x.id === itemId);
    if (!m) return;
    // Update item link + rate + THICKNESS. Size, quantity, margin stay as
    // the user has entered — those are dimensions of the piece, not tied
    // to the item master.
    const rate = Number(m.sale_price) || 0;
    const parsedThk = parseThickness(m.name);
    setDraftItems((prev) => (prev || []).map((it, i) => {
      if (i !== idx) return it;
      return {
        ...it,
        item_id: m.id,
        item_name: m.name,
        // CRITICAL: always overwrite thickness so the big display in the
        // order card / operator panel / PDF matches the newly picked item.
        thickness: parsedThk || it.thickness,
        rate_per_sqft: rate > 0 ? rate : it.rate_per_sqft,
      };
    }));
  };
  const addDraftItem = () => {
    setDraftItems((prev) => [...(prev || []), {
      item_id: null, item_name: "", thickness: "",
      width_in: 48, height_in: 96, quantity: 1, unit: "sqft",
      rate_per_sqft: 250, discount_amount: 0,
      sizing_mode: "standard", margin_inches: 3,
    }]);
  };
  const removeDraftItem = (idx) => {
    setDraftItems((prev) => (prev || []).filter((_, i) => i !== idx));
  };
  const saveEditSheets = async () => {
    if (!draftItems || draftItems.length === 0) {
      toast.error("At least one sheet required"); return;
    }
    setSavingSheets(true);
    try {
      const { data } = await api.patch(`/orders/${orderId}/items`, {
        items: draftItems.map((it) => ({
          item_id: it.item_id || null,
          item_name: it.item_name || "",
          thickness: it.thickness || "",
          rate_per_sqft: Number(it.rate_per_sqft) || 0,
          width_in: Number(it.width_in) || 0,
          height_in: Number(it.height_in) || 0,
          quantity: parseInt(it.quantity, 10) || 1,
          sizing_mode: it.sizing_mode || "standard",
          margin_inches: Number(it.margin_inches) || 3,
          // Send per-axis margins so backend uses W & H separately (falls back
          // to `margin_inches` if either axis field is empty).
          margin_w_inches: it.margin_w_inches !== undefined && it.margin_w_inches !== "" && it.margin_w_inches !== null
            ? Number(it.margin_w_inches)
            : (Number(it.margin_inches) || 3),
          margin_h_inches: it.margin_h_inches !== undefined && it.margin_h_inches !== "" && it.margin_h_inches !== null
            ? Number(it.margin_h_inches)
            : (Number(it.margin_inches) || 3),
          unit: it.unit || "sqft",
          discount_amount: Number(it.discount_amount) || 0,
        })),
      });
      setOrder(data);
      toast.success("Sheets updated. Totals recomputed.");
      setEditSheets(false);
      setDraftItems(null);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setSavingSheets(false); }
  };

  const downloadPdf = (docType) => {
    const tok = getAuthToken() || "";
    window.open(`${API}/orders/${orderId}/pdf?doc_type=${docType}&auth=${encodeURIComponent(tok)}`, "_blank");
  };

  // Share Bill PDF directly to WhatsApp (or any app) via the native share sheet
  // on mobile. On desktops that don't support file-sharing, falls back to a
  // download + wa.me link so the user can drag-and-drop the PDF into chat.
  const [sharingPdf, setSharingPdf] = useState(false);
  const sharePdf = async () => {
    if (!order || sharingPdf) return;
    setSharingPdf(true);
    try {
      const tok = getAuthToken() || "";
      const url = `${API}/orders/${orderId}/pdf?doc_type=invoice&auth=${encodeURIComponent(tok)}`;
      const res = await fetch(url);
      if (!res.ok) throw new Error(`PDF fetch failed (${res.status})`);
      const blob = await res.blob();
      const filename = `Shobhit-${order.order_number}-Bill.pdf`;
      const file = new File([blob], filename, { type: "application/pdf" });

      const trackUrl = `${window.location.origin}/track/${order.order_number}`;
      const balanceStr = order.balance > 0
        ? `Balance: ₹${Math.round(order.balance).toLocaleString("en-IN")}`
        : "Fully Paid ✓";
      const text =
        `*Shobhit Creation* — Order #${order.order_number}\n` +
        `Namaste ${order.customer_name} ji 🙏\n` +
        `Total: ₹${Math.round(order.total_amount).toLocaleString("en-IN")}  |  ${balanceStr}${dueLine}\n` +
        `Track: ${trackUrl}`;

      // Native share with the PDF attached (mobile Chrome / Safari).
      if (typeof navigator !== "undefined"
          && navigator.canShare
          && navigator.canShare({ files: [file] })) {
        try {
          await navigator.share({
            files: [file],
            title: `Bill #${order.order_number}`,
            text,
          });
          return;
        } catch (err) {
          // User cancelled share sheet — do nothing further.
          if (err && err.name === "AbortError") return;
          // Otherwise fall through to the download fallback below.
        }
      }

      // Desktop / unsupported fallback: trigger a download and open WhatsApp Web
      // with the message pre-filled so the user can attach the file from Downloads.
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(blobUrl), 10000);

      const mobile = String(order.mobile || "").replace(/\D/g, "");
      const phone = waPhone(mobile);
      const waUrl = `https://wa.me/${phone}?text=${encodeURIComponent(text)}`;
      window.open(waUrl, "_blank");
      toast.info("PDF download ho gaya — WhatsApp me attach kar dijiye 📎");
    } catch (e) {
      toast.error("PDF share nahi ho paya: " + (e.message || "unknown"));
    } finally {
      setSharingPdf(false);
    }
  };

  const shareWhatsApp = () => {
    if (!order) return;
    const trackUrl = `${window.location.origin}/track/${order.order_number}`;
    const balanceStr = order.balance > 0
      ? `Balance: ₹${order.balance.toLocaleString("en-IN")}`
      : "Fully Paid ✓";
    const text =
      `*Shobhit Creation* — Order #${order.order_number}\n\n` +
      `Namaste ${order.customer_name} ji 🙏\n\n` +
      `Total SqFt: ${order.total_sqft}\n` +
      `Total Amount: ₹${order.total_amount.toLocaleString("en-IN")}\n` +
      `${balanceStr}${dueLine}\n` +
      `Status: ${statusReadable(order.status)}\n\n` +
      `Live tracking + PDF quotation:\n${trackUrl}`;
    const mobile = String(order.mobile || "").replace(/\D/g, "");
    const phone = waPhone(mobile);
    const url = `https://wa.me/${phone}?text=${encodeURIComponent(text)}`;
    window.open(url, "_blank");
  };

  const openSmsDialog = () => {
    if (!order) return;
    const trackUrl = `${window.location.origin}/track/${order.order_number}`;
    // Compact SMS-friendly copy (no emojis, no markdown, single-line breaks).
    const text =
      `Namaste ${order.customer_name}, aapka Shobhit Creation order ${order.order_number} — Status: ${statusReadable(order.status)}. ` +
      (order.balance > 0
        ? `Balance Rs ${Math.round(order.balance)}. `
        : `Fully Paid. `) +
      `Track: ${trackUrl}`;
    setSmsDialog({ mobile: order.mobile || "", message: text, sending: false });
  };

  const sendSms = async () => {
    if (!smsDialog) return;
    if (!smsDialog.mobile.trim() || !smsDialog.message.trim()) {
      toast.error("Mobile and message are required");
      return;
    }
    setSmsDialog((d) => ({ ...d, sending: true }));
    try {
      await api.post("/sms/send", {
        mobile: smsDialog.mobile.trim(),
        message: smsDialog.message.trim(),
        order_id: orderId,
      });
      toast.success(`SMS sent to ${smsDialog.mobile}`);
      setSmsDialog(null);
      await refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "SMS failed");
      setSmsDialog((d) => ({ ...d, sending: false }));
    }
  };


  const shareDesignForApproval = () => {
    if (!order) return;
    const reviewUrl = `${window.location.origin}/design-review/${order.order_number}`;
    const text =
      `*Shobhit Creation* — Order #${order.order_number}\n\n` +
      `Namaste ${order.customer_name} ji, apna design dekhne ke liye link pe click karein:\n\n` +
      `${reviewUrl}`;
    const mobile = String(order.mobile || "").replace(/\D/g, "");
    const phone = waPhone(mobile);
    const url = `https://wa.me/${phone}?text=${encodeURIComponent(text)}`;
    window.open(url, "_blank");
  };

  const openShareModal = () => {
    if (!order) return;
    const reviewUrl = `${window.location.origin}/design-review/${order.order_number}`;
    const imageUrl = `${API}/public/track/${order.order_number}/design-preview`;
    const text =
      `*Shobhit Creation* — Order #${order.order_number}\n\n` +
      `Namaste ${order.customer_name} ji, apna design dekhne ke liye link pe click karein:\n\n` +
      `${reviewUrl}`;
    const mobile = String(order.mobile || "").replace(/\D/g, "");
    const phone = waPhone(mobile);
    setShareModal({
      link: `https://wa.me/${phone}?text=${encodeURIComponent(text)}`,
      imageUrl, reviewUrl, text, phone, customer: order.customer_name,
    });
  };

  // Best-effort direct WhatsApp opener — attempts to open without a modal.
  // Returns true if a window handle was acquired (popup allowed).
  const tryOpenWhatsAppDirect = () => {
    if (!order) return false;
    const reviewUrl = `${window.location.origin}/design-review/${order.order_number}`;
    const text =
      `*Shobhit Creation* — Order #${order.order_number}\n\n` +
      `Namaste ${order.customer_name} ji, apna design dekhne ke liye link pe click karein:\n\n` +
      `${reviewUrl}`;
    const mobile = String(order.mobile || "").replace(/\D/g, "");
    const phone = waPhone(mobile);
    const url = `https://wa.me/${phone}?text=${encodeURIComponent(text)}`;
    try {
      const w = window.open(url, "_blank", "noopener,noreferrer");
      return !!w;
    } catch (_e) {
      return false;
    }
  };

  const bypassApproval = async () => {
    if (!order) return;
    if (!window.confirm(
      `Bypass customer approval for order #${order.order_number}?\n\n` +
      `Ye tab karo jab customer verbally approve kar chuka ho (WhatsApp na use karta ho).\n` +
      `Order NEW ORDER → DESIGN PENDING move ho jayega.`
    )) return;
    setBusy(true);
    try {
      await api.post(`/orders/${orderId}/approve-bypass`);
      toast.success("Approval bypassed — order moved to Design Pending");
      await refresh();
      onUpdated?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const softDeleteOrder = async () => {
    if (!order) return;
    if (!window.confirm(
      `Move order #${order.order_number} to Recycle Bin?\n\nOnly the primary admin can restore or permanently delete it from there.`
    )) return;
    setBusy(true);
    try {
      await api.delete(`/orders/${orderId}`);
      toast.success(`Order #${order.order_number} moved to Recycle Bin`);
      onUpdated?.();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const flowIdx = order ? STATUS_FLOW.indexOf(order.status) : -1;
  const nextStatus = order && !order.plate_flow && flowIdx >= 0 ? STATUS_FLOW[flowIdx + 1] : null;

  const modeLabel = (k) => modes.find((m) => m.key === k)?.label || k;

  return (
    <>
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className={(editingOrder ? "max-w-7xl" : "max-w-5xl") + " bg-slate-900 border-slate-700 text-white rounded-sm max-h-[90vh] overflow-y-auto grid-cols-[minmax(0,1fr)]"}
        data-testid="order-detail-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex flex-wrap items-center gap-x-3 gap-y-1 font-heading min-w-0 pr-6">
            <Package size={22} className="text-orange-400" />
            <span className="font-mono-num text-orange-400">#{order?.order_number || "—"}</span>
            {order?.tax_invoice_no && (
              <span
                className="font-mono-num text-[10px] tracking-widest text-cyan-300 border border-cyan-500/40 bg-cyan-500/10 px-2 py-0.5 rounded-sm"
                title="Tax Invoice number"
                data-testid="order-tax-inv-no"
              >
                {order.tax_invoice_no}
              </span>
            )}
            <span className="text-slate-500">·</span>
            <span className="text-slate-200 break-words min-w-0">{order?.customer_name}</span>
            {isAdmin && order && (
              <button
                type="button"
                onClick={startEditCustomer}
                className="text-slate-500 hover:text-orange-400 transition-colors p-1"
                title="Edit customer name / mobile"
                data-testid="edit-customer-button"
              >
                <PencilSimple size={14} weight="bold" />
              </button>
            )}
            {order && <StatusBadge status={order.status} />}
          </DialogTitle>
        </DialogHeader>

        {!order ? (
          <div className="p-10 text-center text-slate-400 font-mono-num">Loading…</div>
        ) : editingOrder ? (
          <div className="pt-2 bg-slate-900 [&_.surface]:bg-slate-950 [&_.surface]:backdrop-blur-none" data-testid="order-edit-mode">
            <NewOrderForm
              initialOrder={order}
              onSaved={async (fresh) => {
                setOrder(fresh);
                onUpdated?.(fresh);
                setEditingOrder(false);
                // pull latest from server just in case any secondary fields
                // (like status_history entries) need refreshing.
                await refresh();
              }}
              onCancelEdit={() => setEditingOrder(false)}
            />
          </div>
        ) : (
          <div className="space-y-5">
            {order?.billing_mode === "job_work" && <JobWorkBadge testid="detail-job-work-badge" />}
            {isAdmin && user?.role === "admin" && order && <CostingSection order={order} goLiveAt={goLiveAt} />}
            {isAdmin && (
              <div className="flex flex-wrap gap-2" data-testid="order-actions-bar">
                <Button onClick={shareWhatsApp}
                  className="rounded-sm h-10 bg-emerald-600 hover:bg-emerald-500 text-white"
                  data-testid="whatsapp-share-button">
                  <WhatsappLogo size={18} weight="fill" className="mr-1" /> Share on WhatsApp
                </Button>
                <Button onClick={openSmsDialog} variant="outline"
                  className="rounded-sm h-10 border-cyan-500/60 text-cyan-300 hover:bg-cyan-500/10"
                  data-testid="send-sms-button">
                  <ChatCircleText size={18} weight="fill" className="mr-1" /> Send SMS
                </Button>
                <Button onClick={() => downloadPdf("invoice")} variant="outline"
                  className="rounded-sm h-10 border-slate-600 text-slate-200 hover:bg-slate-800"
                  data-testid="pdf-quotation-button">
                  <FileText size={18} className="mr-1" /> Bill PDF
                </Button>
                <Button onClick={sharePdf} disabled={sharingPdf}
                  className="rounded-sm h-10 bg-sky-600 hover:bg-sky-500 text-white disabled:opacity-60"
                  data-testid="share-pdf-button"
                  title="PDF ko seedha WhatsApp pe bhejein — download ki zarurat nahi">
                  <ShareFat size={18} weight="fill" className="mr-1" />
                  {sharingPdf ? "Preparing…" : "Share PDF"}
                </Button>
                <div className="ml-auto flex items-center gap-2">
                  {order.gst_treatment === "regular" || order.gst_treatment === "unregistered_customer" ? (
                    order.gst_locked ? (
                      <span
                        className="font-mono-num text-[10px] tracking-widest text-cyan-300 border border-cyan-500/40 bg-cyan-500/10 px-2 py-1.5 rounded-sm h-10 flex items-center"
                        title={`Tax Invoice ${order.tax_invoice_no} — locked. Use Credit Note to correct.`}
                        data-testid="order-gst-locked-badge"
                      >
                        🔒 GST LOCKED · {order.tax_invoice_no}
                      </span>
                    ) : (
                      <Button
                        onClick={async () => {
                          if (!window.confirm(
                            "Finalize Tax Invoice for this order?\n\n"
                            + "• A new INV-<FY>-XXXX number will be allocated.\n"
                            + "• The order becomes GST-locked — items/rates/discount edits and delete will be blocked.\n"
                            + "• Corrections after this go through Credit Note only.\n\n"
                            + "SC number remains unchanged. Cash/Bank / stock: no change."
                          )) return;
                          try {
                            await api.post(`/orders/${order.id}/finalize-tax-invoice`);
                            const { data } = await api.get(`/orders/${order.id}`);
                            setOrder(data);
                            onUpdated?.(data);
                            toast.success(`Tax Invoice finalized: ${data.tax_invoice_no}`);
                          } catch (e) {
                            toast.error(formatApiError(e.response?.data?.detail));
                          }
                        }}
                        disabled={busy}
                        variant="outline"
                        className="rounded-sm h-10 border-cyan-500/60 text-cyan-300 hover:bg-cyan-500/10"
                        data-testid="finalize-tax-invoice-button"
                      >
                        <FileText size={16} className="mr-1" weight="bold" /> Finalize Tax Invoice
                      </Button>
                    )
                  ) : null}
                  <Button
                    onClick={() => setEditingOrder(true)}
                    disabled={busy || order.gst_locked}
                    variant="outline"
                    className="rounded-sm h-10 border-orange-500/60 text-orange-300 hover:bg-orange-500/10 disabled:opacity-40"
                    data-testid="edit-order-button"
                    title={order.gst_locked ? "GST-locked. Use Credit Note to correct." : ""}
                  >
                    <PencilSimple size={16} className="mr-1" weight="bold" /> Edit Order
                  </Button>
                  <Button
                    onClick={softDeleteOrder}
                    disabled={busy || order.gst_locked}
                    variant="outline"
                    className="rounded-sm h-10 border-red-500/40 text-red-400 hover:bg-red-500/10 hover:text-red-300 disabled:opacity-40"
                    data-testid="soft-delete-order-button"
                    title={order.gst_locked ? "GST-locked. Use Credit Note." : ""}
                  >
                    <Trash size={16} className="mr-1" weight="bold" /> Delete
                  </Button>
                </div>
              </div>
            )}

            {/* Meta */}
            <div className="grid md:grid-cols-4 gap-3 text-sm">
              <Meta label="Mobile" value={
                <div className="flex items-center gap-2">
                  <a href={`tel:${order.mobile}`} className="text-cyan-300 font-mono-num">{order.mobile}</a>
                  {isAdmin && (
                    <button
                      type="button"
                      onClick={startEditCustomer}
                      className="text-slate-500 hover:text-orange-400 transition-colors"
                      title="Edit mobile / name / address"
                      data-testid="edit-mobile-inline-btn"
                    >
                      <PencilSimple size={12} weight="bold" />
                    </button>
                  )}
                </div>
              } />
              <Meta label="Created" value={
                <div className="font-mono-num">
                  {new Date(order.created_at).toLocaleString()}
                  {order.created_by_name && (
                    <div className="text-[10px] text-slate-500 mt-0.5 flex items-center gap-1">
                      <User size={10}/> by {order.created_by_name}
                    </div>
                  )}
                </div>
              } />
              <Meta label="Total SqFt" value={<span className="font-mono-num text-cyan-300">{order.total_sqft}</span>} />
              <Meta label="Balance" value={<span className="font-mono-num text-white">₹{order.balance.toLocaleString("en-IN")}</span>} />
            </div>

            {/* Welder toggle — admin can retroactively mark an existing order
                as a "welder" order (fabricator client) even after the bill has
                been generated. Detach also supported. */}
            {isAdmin && (
              <div className="surface p-3 rounded-sm flex items-center justify-between gap-3 flex-wrap" data-testid="welder-toggle-row">
                <div className="min-w-0">
                  <div className="text-[10px] font-mono-num tracking-widest text-slate-500">CUSTOMER TYPE</div>
                  <div className="text-sm mt-1">
                    {order.customer_type === "welder" && order.welder ? (
                      <span className="inline-flex items-center gap-1.5">
                        <span className="text-[10px] font-mono-num tracking-widest px-2 py-0.5 rounded-sm bg-cyan-500/20 border border-cyan-500/50 text-cyan-300">
                          WELDER
                        </span>
                        <span className="text-slate-200 font-semibold" data-testid="order-welder-name">{order.welder.name}</span>
                        {order.welder.shop_name && (
                          <span className="text-slate-500 text-xs">· {order.welder.shop_name}</span>
                        )}
                      </span>
                    ) : (
                      <span className="text-slate-400">One-time customer</span>
                    )}
                  </div>
                </div>
                <label className="inline-flex items-center gap-2 cursor-pointer bg-slate-800/60 hover:bg-slate-800 border border-slate-700 rounded-sm px-3 py-2" data-testid="welder-toggle-label">
                  <input
                    type="checkbox"
                    checked={order.customer_type === "welder"}
                    disabled={savingWelder}
                    onChange={(e) => toggleWelder(e.target.checked)}
                    className="accent-cyan-500 w-4 h-4"
                    data-testid="welder-toggle-checkbox"
                  />
                  <span className="text-xs font-mono-num tracking-widest text-slate-200">MARK AS WELDER</span>
                </label>
              </div>
            )}

            {order.address && (
              <div className="surface p-3 rounded-sm border-l-2 border-cyan-500/50 flex items-start gap-2 text-sm">
                <div className="text-[10px] font-mono-num tracking-widest text-slate-500 mt-0.5">ADDRESS</div>
                <div className="text-slate-200 whitespace-pre-wrap flex-1" data-testid="order-address">
                  {order.address}
                </div>
              </div>
            )}

            {/* Items */}
            <div className="surface p-4 rounded-sm">
              <div className="flex items-center justify-between mb-3">
                <div className="text-xs font-mono-num tracking-widest text-slate-400">// SHEETS</div>
                {(
                  editSheets ? (
                    <div className="flex items-center gap-2">
                      <Button
                        onClick={cancelEditSheets}
                        variant="outline"
                        className="rounded-sm h-8 px-3 text-xs border-slate-600 text-slate-300 hover:bg-slate-800"
                        data-testid="cancel-edit-sheets"
                      >Cancel</Button>
                      <Button
                        onClick={saveEditSheets}
                        disabled={savingSheets}
                        className="rounded-sm h-8 px-3 text-xs btn-industrial"
                        data-testid="save-edit-sheets"
                      >{savingSheets ? "SAVING…" : "SAVE CHANGES"}</Button>
                    </div>
                  ) : (
                    <Button
                      onClick={startEditSheets}
                      className="rounded-sm h-8 px-3 text-xs bg-orange-500 hover:bg-orange-600 text-white shadow"
                      data-testid="edit-sheets-btn"
                    >
                      <PencilSimple size={14} weight="bold" className="mr-1" />
                      EDIT SHEETS / SIZE
                    </Button>
                  )
                )}
              </div>
              {editSheets ? (
                <EditableSheetsTable
                  items={draftItems || []}
                  materialItems={materialItems}
                  updateItem={updateDraftItem}
                  pickItem={pickDraftItem}
                  addItem={addDraftItem}
                  removeItem={removeDraftItem}
                />
              ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-slate-500 font-mono-num text-xs">
                    <tr className="border-b border-slate-700">
                      <th className="text-left py-2 pr-2">#</th>
                      <th className="text-left">THK</th>
                      <th className="text-left">SIZING</th>
                      <th className="text-right">W (in)</th>
                      <th className="text-right">H (in)</th>
                      <th className="text-right">QTY</th>
                      <th className="text-right">SQFT</th>
                      <th className="text-right">RATE</th>
                      <th className="text-right">AMOUNT</th>
                    </tr>
                  </thead>
                  <tbody className="font-mono-num">
                    {order.items.map((it, i) => (
                      <tr key={i} className="border-b border-slate-800">
                        <td className="py-2 pr-2 text-slate-500">{i + 1}</td>
                        <td className="text-orange-300">
                          {it.thickness}
                          {it.item_name && (
                            <div className="text-[10px] text-slate-400 mt-0.5 normal-case tracking-normal">
                              {it.item_name}
                            </div>
                          )}
                        </td>
                        <td className="text-slate-400 text-[10px] uppercase">{it.sizing_mode_w === "exact" && it.sizing_mode_h === "exact" ? "exact" : (it.sizing_mode || "exact")}</td>
                        <td className="text-right">
                          <span className="font-bold text-white">{it.width_in_raw ?? it.width_in}</span>
                          {it.width_in_raw != null && it.width_in_raw !== it.width_in && (
                            <span className="text-[10px] text-slate-600 ml-1">({it.width_in})</span>
                          )}
                        </td>
                        <td className="text-right">
                          <span className="font-bold text-white">{it.height_in_raw ?? it.height_in}</span>
                          {it.height_in_raw != null && it.height_in_raw !== it.height_in && (
                            <span className="text-[10px] text-slate-600 ml-1">({it.height_in})</span>
                          )}
                        </td>
                        <td className="text-right">{it.quantity}</td>
                        <td className="text-right text-cyan-300">{it.total_sqft}</td>
                        <td className="text-right">
                          {isAdmin ? (
                            <QuickRateCell
                              orderId={orderId}
                              itemIndex={i}
                              currentRate={it.rate_per_sqft}
                              onSaved={(data) => { setOrder(data); onUpdated && onUpdated(data); }}
                            />
                          ) : (
                            <>₹{it.rate_per_sqft}</>
                          )}
                        </td>
                        <td className="text-right text-orange-400">₹{it.total_amount.toLocaleString("en-IN")}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="font-mono-num">
                    <tr>
                      <td colSpan="8" className="text-right pt-3 text-slate-400">TOTAL</td>
                      <td className="text-right pt-3 text-white text-lg font-bold">₹{order.total_amount.toLocaleString("en-IN")}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
              )}
            </div>

            {/* Reference Images */}
            <div className="surface p-4 rounded-sm space-y-3">
              <div className="flex items-center justify-between">
                <div className="text-xs font-mono-num tracking-widest text-slate-400 flex items-center gap-2">
                  <ImageSquare size={14} /> // REFERENCE IMAGES ({order.reference_images?.length || 0})
                </div>
                {isAdmin && (
                  <label className="cursor-pointer">
                    <input ref={refRef} type="file" hidden accept="image/*" multiple
                      onChange={(e) => uploadMultiple(e.target.files)}
                      data-testid="ref-image-upload-input"/>
                    <span className="inline-flex items-center gap-1 text-xs text-slate-200 border border-slate-600 hover:bg-slate-800 px-3 py-1.5 rounded-sm">
                      <Plus size={12} /> Add Images
                    </span>
                  </label>
                )}
              </div>
              {(!order.reference_images || order.reference_images.length === 0) ? (
                <div className="text-slate-500 text-sm">
                  No reference images. {isAdmin && "Upload images the customer shared."}
                </div>
              ) : (
                <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                  {order.reference_images.map((img) => (
                    <div key={img.id} className="relative aspect-square surface-2 rounded-sm overflow-hidden group">
                      <img src={refImgUrl(img.id)} alt={img.original_filename}
                        onClick={() => setLightbox({ url: refImgUrl(img.id), name: img.original_filename })}
                        className="w-full h-full object-cover cursor-zoom-in"/>
                      {isAdmin && (
                        <button
                          onClick={() => deleteRefImage(img.id)}
                          className="absolute top-1 right-1 bg-slate-900/80 hover:bg-red-500 rounded-sm p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                          data-testid={`delete-ref-image-${img.id}`}
                        >
                          <X size={12} className="text-white" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
              {/* Design notes live right below the reference images so the operator
                  can read what to change while still looking at the design.
                  Admin can inline-edit even after sale — works on ALL statuses. */}
              <DesignNotesEditor
                order={order}
                isAdmin={isAdmin}
                onSaved={refresh}
              />
            </div>

            {/* Fabrication department assignment + advance ledger — gate/railing only */}
            {order.project_type === "gate_railing" && isAdmin && (
              <FabricationPanel order={order} onChanged={refresh} />
            )}

            {/* Phase 2: CNC Cutting Sizes (fabricator size photos → admin CNC parts).
                Shows once fabricator has uploaded photos (size_uploaded) or after
                admin confirmation (size_confirmed) so owner can review. */}
            {order.project_type === "gate_railing" && order.plate_flow && !order.gate_v2 && (
              <CuttingPlatesSection order={order} mode="admin" onChanged={refresh} canUnlock={user?.role === "admin"} />
            )}
            {order.gate_v2 && (
              <GateSheetSizes order={order} mode="admin" onChanged={refresh} canUnlock={user?.role === "admin"} />
            )}
            {order.gate_v2 && ["admin", "secondary_admin"].includes(user?.role) && (
              <GateCosting order={order} isAdmin={user?.role === "admin"} />
            )}
            {order.gate_v2 && (
              <label className="surface p-3 rounded-sm flex items-center gap-2 text-sm text-slate-200" data-testid="order-attach-spec">
                <input type="checkbox" checked={order.attach_spec !== false} data-testid="order-attach-spec-checkbox"
                  onChange={async (e) => { try { await api.patch(`/orders/${order.id}/attach-spec`, { attach_spec: e.target.checked }); refresh(); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } }} />
                Attach spec sheets to bill PDF
              </label>
            )}
            {order.project_type === "gate_railing" && isAdmin && !order.plate_flow &&
              ["size_uploaded", "size_confirmed"].includes(order.status) && (
              <CNCCuttingSizesPanel order={order} onChanged={refresh} />
            )}

            {/* Files + Status */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="surface p-4 rounded-sm space-y-3">
                <div className="text-xs font-mono-num tracking-widest text-slate-400">// DESIGN &amp; CUTTING FILES</div>

                {/* Approval status banner */}
                {order.design_preview && order.design_approval_status === "pending" && (
                  <div className="rounded-sm border-2 border-cyan-500/60 bg-cyan-500/10 p-3" data-testid="approval-pending-banner">
                    <div className="text-xs font-mono-num tracking-widest text-cyan-300">
                      // AWAITING CUSTOMER APPROVAL
                    </div>
                    <div className="text-slate-200 text-sm mt-1">
                      Design upload ho gaya. Customer ko WhatsApp pe review link bhejein:
                    </div>
                    <Button
                      onClick={openShareModal}
                      className="mt-2 w-full h-10 rounded-sm bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-heading tracking-widest"
                      data-testid="share-design-approval-btn"
                    >
                      <WhatsappLogo size={16} weight="fill" className="mr-2"/> SHARE FOR CUSTOMER APPROVAL
                    </Button>
                    <Button
                      onClick={bypassApproval}
                      disabled={busy}
                      variant="outline"
                      className="mt-2 w-full h-9 rounded-sm border-amber-500/50 text-amber-300 hover:bg-amber-500/10 text-[11px] font-mono-num tracking-widest"
                      data-testid="bypass-approval-btn"
                    >
                      Bypass — Mark Approved Manually
                    </Button>
                    <div className="text-[10px] font-mono-num text-slate-500 mt-1 leading-tight">
                      Use bypass only if customer cannot use WhatsApp / has approved verbally.
                    </div>
                  </div>
                )}
                {order.design_approval_status === "confirmed" && (
                  <div className="rounded-sm border border-emerald-500/50 bg-emerald-500/10 px-3 py-2 flex items-center gap-2 text-xs font-mono-num text-emerald-300">
                    <CheckCircle size={14} weight="fill"/>
                    CUSTOMER CONFIRMED DESIGN ✓
                    {order.design_approval_bypassed_by_name && (
                      <span className="text-amber-300 ml-1">
                        (bypassed by {order.design_approval_bypassed_by_name})
                      </span>
                    )}
                  </div>
                )}
                {order.design_approval_status === "rejected" && (
                  <div className="rounded-sm border-2 border-amber-500/70 bg-amber-500/15 p-3" data-testid="approval-rejected-banner">
                    <div className="flex items-center gap-2 text-amber-300 font-heading tracking-widest text-xs">
                      <X size={14} weight="bold"/> CUSTOMER REJECTED DESIGN — UPLOAD DESIGN AGAIN
                    </div>
                    {order.design_rejection_reason && (
                      <div className="text-slate-200 text-xs mt-1.5 whitespace-pre-wrap">
                        Reason: <b className="text-amber-200">{order.design_rejection_reason}</b>
                      </div>
                    )}
                  </div>
                )}

                <FileRow
                  label={order.design_approval_status === "rejected" ? "Upload New Design (again)" : "Design Preview (image or PDF)"}
                  hint={order.design_approval_status === "rejected"
                    ? "Customer ne pichhla design reject kiya — naya banake upload karein"
                    : ((order.design_previews?.length || 0) > 1
                        ? `${order.design_previews.length} designs uploaded · latest shown · aur upload karne ke liye tap karo`
                        : "What you send customer for approval · multiple files select kar sakte ho")}
                  icon={<Eye size={18} className={order.design_approval_status === "rejected" ? "text-amber-400" : "text-cyan-300"} />}
                  file={order.design_preview}
                  onUpload={() => previewRef.current?.click()}
                  onDownload={() => download("design_preview")}
                  onView={() => preview("design_preview")}
                  isAdmin={isAdmin}
                />
                {(order.design_previews?.length || 0) > 1 && (
                  <div
                    className="rounded-sm border border-slate-700 bg-slate-800/40 p-3"
                    data-testid="design-previews-strip"
                  >
                    <div className="text-[10px] font-mono-num tracking-widest text-slate-400 mb-2 flex items-center justify-between">
                      <span>ALL DESIGNS ({order.design_previews.length})</span>
                      <span className="text-slate-500 normal-case tracking-normal">Latest is primary · customer sabhi dekh sakega</span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {order.design_previews.map((d, i) => {
                        const isPrimary = order.design_preview?.id === d.id;
                        const isImg = (d.content_type || "").startsWith("image/");
                        const url = `${API}/orders/${order.id}/design/${d.id}?auth=${encodeURIComponent(getAuthToken() || "")}`;
                        return (
                          <div
                            key={d.id}
                            className={
                              "relative w-24 h-24 rounded-sm border-2 overflow-hidden bg-slate-900 " +
                              (isPrimary ? "border-orange-500" : "border-slate-700")
                            }
                            data-testid={`design-preview-thumb-${d.id}`}
                          >
                            {isImg ? (
                              <img
                                src={url}
                                alt={d.original_filename || `Design ${i + 1}`}
                                className="w-full h-full object-cover cursor-pointer"
                                onClick={() => setLightbox({ url, name: d.original_filename || `Design ${i + 1}` })}
                              />
                            ) : (
                              <div className="w-full h-full flex flex-col items-center justify-center text-slate-500 text-[10px] p-1 text-center">
                                <FileArrowUp size={22} weight="thin" />
                                PDF
                              </div>
                            )}
                            <div className="absolute top-0.5 left-0.5 bg-slate-900/85 text-slate-200 rounded-sm px-1 text-[9px] font-mono-num">
                              #{i + 1}
                            </div>
                            {isPrimary && (
                              <div className="absolute bottom-0.5 left-0.5 bg-orange-500 text-white rounded-sm px-1 text-[8px] font-mono-num tracking-widest">
                                PRIMARY
                              </div>
                            )}
                            {isAdmin && (
                              <button
                                type="button"
                                onClick={async () => {
                                  if (!window.confirm(`Delete design #${i + 1}?`)) return;
                                  try {
                                    await api.delete(`/orders/${order.id}/design/${d.id}`);
                                    toast.success("Design removed");
                                    await refresh();
                                  } catch (e) {
                                    toast.error(formatApiError(e.response?.data?.detail));
                                  }
                                }}
                                className="absolute top-0.5 right-0.5 bg-slate-900/85 hover:bg-red-500 rounded-sm p-0.5"
                                data-testid={`design-preview-delete-${d.id}`}
                                title="Delete"
                              >
                                <X size={10} className="text-white" />
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                <input ref={previewRef} type="file" hidden accept="image/*,.pdf" multiple
                  data-testid="preview-upload-input"
                  onChange={(e) => {
                    const files = Array.from(e.target.files || []);
                    if (files.length === 0) return;
                    // CRITICAL: Open blank window synchronously inside the change
                    // handler so it counts as a user-gesture-driven popup.
                    // We'll navigate this window to WhatsApp after upload finishes.
                    let waWindow = null;
                    try {
                      waWindow = window.open("about:blank", "_blank");
                      if (waWindow) {
                        try {
                          waWindow.document.write(
                            '<!doctype html><title>Opening WhatsApp…</title>' +
                            '<style>body{background:#0f172a;color:#fff;font-family:system-ui,sans-serif;' +
                            'display:flex;align-items:center;justify-content:center;height:100vh;margin:0;text-align:center}</style>' +
                            '<div><div style="font-size:22px;font-weight:800;letter-spacing:.15em">SHOBHIT CREATION</div>' +
                            '<div style="margin-top:14px;color:#94a3b8">Uploading ' + files.length + ' design' + (files.length > 1 ? 's' : '') + ' & preparing WhatsApp…</div></div>'
                          );
                        } catch (_e) { /* cross-origin edge case, ignore */ }
                      }
                    } catch (_e) { waWindow = null; }
                    // Sequentially upload each file (backend pushes each into
                    // `design_previews` array; primary `design_preview` is the
                    // latest one). WhatsApp is triggered once at the end.
                    (async () => {
                      for (let i = 0; i < files.length; i++) {
                        // Only open WhatsApp on the LAST file
                        const isLast = i === files.length - 1;
                        // eslint-disable-next-line no-await-in-loop
                        await upload("design_preview", files[i], isLast ? waWindow : null);
                      }
                    })();
                    // Reset input so re-selecting same file still fires change
                    e.target.value = "";
                  }}/>

                <FileRow
                  label="Cutting File (DXF/DWG)"
                  hint="Required to mark Ready to Cut"
                  icon={<FileArrowUp size={18} className="text-orange-500" />}
                  file={order.cutting_file}
                  onUpload={() => dxfRef.current?.click()}
                  onDownload={() => download("cutting_file")}
                  isAdmin={isAdmin}
                />
                <input ref={dxfRef} type="file" hidden accept=".dxf,.dwg"
                  data-testid="dxf-upload-input"
                  onChange={(e) => upload("cutting_file", e.target.files?.[0])}/>
              </div>

              {isAdmin && (
                <div className="surface p-4 rounded-sm space-y-4">
                  <div className="text-xs font-mono-num tracking-widest text-slate-400">// STATUS FLOW</div>
                  <StatusTimeline currentStatus={order.status} gate={!!order.plate_flow} />
                  {nextStatus && (
                    <Button
                      onClick={() => changeStatus(nextStatus)}
                      disabled={busy}
                      className="w-full btn-industrial rounded-sm h-10"
                      data-testid="advance-status-button"
                    >
                      {STATUS_LABELS[nextStatus]} <ArrowRight size={16} className="ml-2" />
                    </Button>
                  )}
                  {isAdmin && (
                    <HoldToggle order={order} onDone={refresh} />
                  )}
                </div>
              )}
            </div>

            {/* Payments */}
            {isAdmin && (
              <div className="surface p-4 rounded-sm space-y-3">
                <div className="text-xs font-mono-num tracking-widest text-slate-400 flex items-center gap-2">
                  <CurrencyInr size={14} /> // PAYMENTS
                </div>

                {/* Balance summary bar */}
                <div
                  className={
                    "grid grid-cols-4 gap-2 rounded-sm border p-3 " +
                    (order.balance > 0
                      ? "border-orange-500/40 bg-orange-500/5"
                      : "border-emerald-500/40 bg-emerald-500/5")
                  }
                  data-testid="balance-summary"
                >
                  <div>
                    <div className="text-[10px] font-mono-num text-slate-500 tracking-widest">TOTAL</div>
                    <div className="font-mono-num text-lg text-slate-200">
                      ₹{order.total_amount.toLocaleString("en-IN")}
                    </div>
                  </div>
                  <div>
                    <div className="text-[10px] font-mono-num text-slate-500 tracking-widest">RECEIVED</div>
                    <div className="font-mono-num text-lg text-cyan-300">
                      ₹{order.advance_paid.toLocaleString("en-IN")}
                    </div>
                  </div>
                  <div>
                    <div className="text-[10px] font-mono-num text-amber-500 tracking-widest">DISCOUNT</div>
                    {(() => {
                      const line = (order.items || []).reduce((t, it) => t + (Number(it.discount_amount) || 0), 0);
                      const settle = Number(order.discount) || 0;
                      return (<>
                        <div className="font-mono-num text-lg text-amber-300" data-testid="discount-amount">
                          ₹{(Math.round((line + settle) * 100) / 100).toLocaleString("en-IN")}
                        </div>
                        {line > 0 && (
                          <div className="text-[10px] font-mono-num text-slate-500" data-testid="discount-breakdown">
                            Line ₹{line.toLocaleString("en-IN")}{settle > 0 ? ` · Settle ₹${settle.toLocaleString("en-IN")}` : ""}
                          </div>
                        )}
                      </>);
                    })()}
                  </div>
                  <div>
                    <div className="text-[10px] font-mono-num text-slate-500 tracking-widest">
                      {order.balance > 0 ? "PENDING" : "FULLY PAID"}
                    </div>
                    <div
                      className={
                        "font-mono-num text-2xl font-bold " +
                        (order.balance > 0 ? "text-orange-400" : "text-emerald-400")
                      }
                      data-testid="pending-balance"
                    >
                      ₹{order.balance.toLocaleString("en-IN")}
                    </div>
                  </div>
                </div>
                {prevDue && (
                  <div className="grid grid-cols-2 gap-3 border border-red-500/30 bg-red-500/5 rounded-sm p-3" data-testid="party-due-box">
                    <div>
                      <div className="text-[10px] font-mono-num text-slate-500 tracking-widest">{prevDue.previous_due > 0 ? "PREVIOUS DUE" : "PREVIOUS ADVANCE"}</div>
                      <div className={"font-mono-num text-lg " + (prevDue.previous_due > 0 ? "text-red-300" : "text-emerald-300")} data-testid="previous-due">
                        {prevDue.previous_due > 0 ? "" : "−"}₹{inr(Math.abs(prevDue.previous_due)).toLocaleString("en-IN")}
                      </div>
                    </div>
                    <div>
                      <div className="text-[10px] font-mono-num text-slate-500 tracking-widest">TOTAL DUE (customer)</div>
                      <div className="font-mono-num text-2xl font-bold text-red-400" data-testid="total-party-due">₹{inr(prevDue.party_balance).toLocaleString("en-IN")}</div>
                    </div>
                  </div>
                )}

                <div className="space-y-1 max-h-56 overflow-y-auto">
                  {order.payments.length === 0 && (order.discounts || []).length === 0 && (
                    <div className="text-slate-500 text-sm">No payments logged</div>
                  )}
                  {order.payments.map((p) => (
                    <div key={p.id} className="flex items-center justify-between text-sm font-mono-num border-b border-slate-800 py-1.5">
                      <div className="flex flex-col">
                        <span className="text-slate-400">{new Date(p.created_at).toLocaleString()}</span>
                        {p.recorded_by_name && (
                          <span className="text-[10px] text-slate-600 flex items-center gap-1">
                            <User size={9}/> by {p.recorded_by_name}
                          </span>
                        )}
                      </div>
                      <span className="text-slate-300">{modeLabel(p.mode)}</span>
                      <span className="text-orange-400">₹{p.amount.toLocaleString("en-IN")}</span>
                    </div>
                  ))}
                  {(order.discounts || []).map((d) => (
                    <div key={d.id} className="flex items-center justify-between text-sm font-mono-num border-b border-slate-800 py-1.5 bg-amber-500/5 group" data-testid={`discount-entry-${d.id}`}>
                      <div className="flex flex-col min-w-[110px]">
                        <span className="text-amber-300 text-xs">{new Date(d.created_at).toLocaleString()}</span>
                        {d.recorded_by_name && (
                          <span className="text-[10px] text-slate-600 flex items-center gap-1">
                            <User size={9}/> by {d.recorded_by_name}
                          </span>
                        )}
                        {d.edited_at && (
                          <span className="text-[9px] text-orange-500/70">(edited)</span>
                        )}
                      </div>
                      <span className="text-amber-300 flex-1 text-center">Discount / Write-off</span>
                      <div className="flex items-center gap-2">
                        <span className="text-amber-300">−₹{d.amount.toLocaleString("en-IN")}</span>
                        <button
                          type="button"
                          onClick={() => setEditDiscount({ id: d.id, amount: d.amount, note: d.note || "" })}
                          className="h-7 w-7 rounded-sm border border-slate-700 hover:border-orange-500 hover:bg-orange-500/10 text-slate-400 hover:text-orange-300 inline-flex items-center justify-center transition-colors"
                          title="Edit discount"
                          data-testid={`edit-discount-${d.id}`}
                        ><PencilSimple size={12} /></button>
                        <button
                          type="button"
                          onClick={async () => {
                            if (!window.confirm(`Delete discount of ₹${d.amount}? Order balance will increase back by this amount.`)) return;
                            try {
                              await api.delete(`/orders/${orderId}/discounts/${d.id}`);
                              toast.success("Discount deleted");
                              await refresh();
                            } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
                          }}
                          className="h-7 w-7 rounded-sm border border-slate-700 hover:border-red-500 hover:bg-red-500/10 text-slate-400 hover:text-red-300 inline-flex items-center justify-center transition-colors"
                          title="Delete discount"
                          data-testid={`delete-discount-${d.id}`}
                        ><TrashSimple size={12} /></button>
                      </div>
                    </div>
                  ))}
                </div>

                <form onSubmit={addPayment} className="grid grid-cols-2 md:grid-cols-5 gap-2 pt-2">
                  <div className="col-span-1">
                    <Label className="text-slate-400 font-mono-num text-[10px] tracking-widest">AMOUNT</Label>
                    <Input
                      type="number" min="0" step="0.01"
                      value={payAmt} onChange={(e) => setPayAmt(e.target.value)}
                      placeholder={order.balance > 0 ? `${order.balance}` : "0"}
                      className="bg-slate-800/60 border-slate-600 text-white h-10 rounded-sm font-mono-num"
                      data-testid="payment-amount-input"
                    />
                  </div>
                  <div className="col-span-1">
                    <Label className="text-slate-400 font-mono-num text-[10px] tracking-widest">DATE</Label>
                    <input
                      type="date"
                      value={payDate}
                      onChange={(e) => setPayDate(e.target.value)}
                      max={istToday()}
                      className="w-full bg-slate-800/60 border border-slate-600 hover:border-orange-500/60 focus:border-orange-500 focus:outline-none text-white h-10 rounded-sm font-mono-num px-2"
                      data-testid="payment-date-input"
                      title="Payment kis din receive hua? Back-date bhi de sakte hain."
                    />
                  </div>
                  <div className="col-span-2 md:col-span-2">
                    <Label className="text-slate-400 font-mono-num text-[10px] tracking-widest">MODE</Label>
                    <Select value={payMode} onValueChange={setPayMode}>
                      <SelectTrigger className="bg-slate-800/60 border-slate-600 text-white h-10 rounded-sm" data-testid="payment-mode-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-900 border-slate-600 text-white">
                        {modes.map((m) => (
                          <SelectItem key={m.key} value={m.key}>{m.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-2 md:col-span-1 flex items-end">
                    <Button type="submit" disabled={busy} className="w-full btn-industrial rounded-sm h-10" data-testid="add-payment-button">
                      Payment In
                    </Button>
                  </div>
                </form>

                {/* Quick-fill full balance */}
                {order.balance > 0 && (
                  <Button
                    type="button"
                    onClick={() => setPayAmt(String(order.balance))}
                    variant="outline"
                    className="w-full rounded-sm border-orange-500/60 text-orange-400 hover:bg-orange-500/10 hover:text-orange-300 h-11 font-heading tracking-widest"
                    data-testid="pay-full-balance-button"
                  >
                    <CurrencyInr size={16} className="mr-1" />
                    PAY FULL BALANCE — ₹{order.balance.toLocaleString("en-IN")}
                  </Button>
                )}

                {/* Mark delivered helper — appears when fully paid AND order is completed */}
                {order.balance <= 0 && order.status === "completed" && (
                  <Button
                    type="button"
                    onClick={() => changeStatus("delivered")}
                    disabled={busy}
                    className="w-full rounded-sm h-11 bg-emerald-600 hover:bg-emerald-500 text-white font-heading tracking-widest"
                    data-testid="mark-delivered-button"
                  >
                    ✓ MARK DELIVERED &amp; CLOSE ORDER
                  </Button>
                )}
              </div>
            )}

            {/* Generate Customer Bill — for welder / middleman resellers.
                Prints a one-off PDF at a HIGHER (or per-item) rate WITHOUT
                touching the internal order price. Not saved to DB. */}
            <div className="mt-3 pt-3 border-t border-dashed border-slate-700 flex justify-center">
              <Button
                onClick={() => setCustomBillOpen(true)}
                variant="outline"
                className="rounded-sm h-10 border-orange-500/60 text-orange-300 hover:bg-orange-500/10 hover:border-orange-500 font-heading tracking-widest"
                data-testid="generate-customer-bill-btn"
              >
                <FileText size={16} className="mr-2" /> GENERATE CUSTOMER BILL
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>

    {/* Custom / Customer bill dialog (one-off PDF with markup) */}
    <CustomerBillDialog
      order={order}
      open={customBillOpen}
      onClose={() => setCustomBillOpen(false)}
    />

    {/* Edit customer dialog — name/mobile/address correction */}
    <Dialog open={editCustomer} onOpenChange={(v) => !v && setEditCustomer(false)}>
      <DialogContent
        className="max-w-md bg-slate-900 border-slate-700 text-white rounded-sm"
        data-testid="edit-customer-dialog"
      >
        <DialogHeader>
          <div className="text-[10px] font-mono-num tracking-widest text-slate-400">// EDIT CUSTOMER</div>
          <DialogTitle className="font-heading text-xl">Fix name or mobile</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-[10px] font-mono-num text-slate-500 tracking-widest">CUSTOMER NAME</Label>
            <Input
              value={custDraft.customer_name}
              onChange={(e) => setCustDraft({ ...custDraft, customer_name: e.target.value })}
              className="bg-slate-800/60 border-slate-600 text-white h-10 rounded-sm"
              data-testid="edit-customer-name"
            />
          </div>
          <div>
            <Label className="text-[10px] font-mono-num text-slate-500 tracking-widest">MOBILE</Label>
            <Input
              type="tel"
              value={custDraft.mobile}
              onChange={(e) => setCustDraft({ ...custDraft, mobile: e.target.value })}
              onBlur={() => setCustDraft((d) => ({ ...d, mobile: cleanPhoneInput(d.mobile) }))}
              className="bg-slate-800/60 border-slate-600 text-white h-10 rounded-sm font-mono-num"
              data-testid="edit-customer-mobile"
            />
          </div>
          <div>
            <Label className="text-[10px] font-mono-num text-slate-500 tracking-widest">ADDRESS (optional)</Label>
            <Input
              value={custDraft.address}
              onChange={(e) => setCustDraft({ ...custDraft, address: e.target.value })}
              className="bg-slate-800/60 border-slate-600 text-white h-10 rounded-sm"
              data-testid="edit-customer-address"
            />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button
              variant="outline"
              onClick={() => setEditCustomer(false)}
              className="rounded-sm h-9 border-slate-600 text-slate-200 hover:bg-slate-800"
              data-testid="edit-customer-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={saveEditCustomer}
              disabled={savingCust}
              className="rounded-sm h-9 bg-orange-500 hover:bg-orange-600 text-white"
              data-testid="edit-customer-save"
            >
              {savingCust ? "Saving…" : "SAVE"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>

    {/* Welder picker — choose an existing welder or create a new one to attach
        to this order retroactively. */}
    <Dialog open={pickWelderOpen} onOpenChange={(v) => !v && setPickWelderOpen(false)}>
      <DialogContent
        className="max-w-md bg-slate-900 border-slate-700 text-white rounded-sm max-h-[80vh] overflow-hidden flex flex-col"
        data-testid="welder-picker-dialog"
      >
        <DialogHeader>
          <div className="text-[10px] font-mono-num tracking-widest text-slate-400">// MARK AS WELDER</div>
          <DialogTitle className="font-heading text-xl">Pick a welder to attach</DialogTitle>
        </DialogHeader>
        <div className="space-y-2 flex-1 overflow-hidden flex flex-col">
          <Input
            value={welderQ}
            onChange={(e) => setWelderQ(e.target.value)}
            placeholder="Search welder name / shop / mobile…"
            className="bg-slate-800/60 border-slate-600 text-white h-10 rounded-sm"
            data-testid="welder-picker-search"
          />
          <div className="flex-1 overflow-y-auto -mr-1 pr-1 space-y-1">
            {welderOptions
              .filter((w) => {
                const s = welderQ.trim().toLowerCase();
                if (!s) return true;
                return (w.name || "").toLowerCase().includes(s) ||
                  (w.shop_name || "").toLowerCase().includes(s) ||
                  (w.mobile || "").toLowerCase().includes(s);
              })
              .map((w) => (
                <button
                  key={w.id}
                  onClick={() => attachWelder(w.id)}
                  disabled={savingWelder}
                  className="w-full text-left surface-2 hover:bg-slate-800 rounded-sm p-3 border border-slate-800"
                  data-testid={`welder-pick-${w.id}`}
                >
                  <div className="text-slate-100 font-semibold">{w.name}</div>
                  <div className="text-[11px] font-mono-num text-slate-500">
                    {w.shop_name ? `${w.shop_name} · ` : ""}{w.mobile}
                  </div>
                </button>
              ))}
            {welderOptions.length === 0 && (
              <div className="text-slate-500 text-sm py-8 text-center">
                Koi welder registered nahi hai. Neeche &quot;Create new&quot; pe click karo.
              </div>
            )}
          </div>
          <div className="flex justify-between items-center gap-2 pt-2 border-t border-slate-800">
            <Button
              variant="outline"
              onClick={() => setPickWelderOpen(false)}
              className="rounded-sm h-9 border-slate-600 text-slate-200 hover:bg-slate-800"
              data-testid="welder-picker-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={attachAsNewWelder}
              disabled={savingWelder}
              className="rounded-sm h-9 bg-emerald-600 hover:bg-emerald-500 text-white"
              data-testid="welder-picker-create-new"
            >
              + Create new welder
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>

    {/* Send SMS dialog (Twilio) */}
    <Dialog open={!!smsDialog} onOpenChange={(o) => { if (!o) setSmsDialog(null); }}>
      <DialogContent className="max-w-md bg-slate-900 border-slate-700" data-testid="sms-dialog">
        <DialogHeader>
          <DialogTitle className="text-white font-heading flex items-center gap-2">
            <ChatCircleText size={20} weight="fill" className="text-cyan-400" /> Send SMS to Customer
          </DialogTitle>
        </DialogHeader>
        {smsDialog && (
          <div className="space-y-4">
            <div>
              <div className="text-xs font-mono-num tracking-widest text-slate-400 mb-1">MOBILE</div>
              <input
                type="tel"
                value={smsDialog.mobile}
                onChange={(e) => setSmsDialog({ ...smsDialog, mobile: e.target.value })}
                className="w-full bg-slate-950 border border-slate-700 rounded-sm p-2 text-white font-mono-num"
                data-testid="sms-dialog-mobile"
              />
              <div className="text-xs text-slate-500 mt-1">10-digit Indian mobile (auto +91) or full E.164 (+…)</div>
            </div>
            <div>
              <div className="text-xs font-mono-num tracking-widest text-slate-400 mb-1">
                MESSAGE <span className="text-slate-500 normal-case">({smsDialog.message.length}/1500)</span>
              </div>
              <textarea
                value={smsDialog.message}
                onChange={(e) => setSmsDialog({ ...smsDialog, message: e.target.value })}
                rows={5}
                className="w-full bg-slate-950 border border-slate-700 rounded-sm p-2 text-white text-sm"
                data-testid="sms-dialog-message"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setSmsDialog(null)}
                className="rounded-sm h-9 border-slate-600 text-slate-300 hover:bg-slate-800"
              >
                Cancel
              </Button>
              <Button
                onClick={sendSms}
                disabled={smsDialog.sending}
                className="rounded-sm h-9 bg-cyan-600 hover:bg-cyan-500 text-white"
                data-testid="sms-dialog-send"
              >
                {smsDialog.sending ? "Sending…" : "Send SMS"}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>

    {/* Edit Discount dialog */}
    <Dialog open={!!editDiscount} onOpenChange={(o) => { if (!o) setEditDiscount(null); }}>
      <DialogContent className="max-w-md bg-slate-900 border-slate-700" data-testid="edit-discount-dialog">
        <DialogHeader>
          <DialogTitle className="text-white font-heading">Edit Discount</DialogTitle>
        </DialogHeader>
        {editDiscount && (
          <div className="space-y-4">
            <div>
              <div className="text-xs font-mono-num tracking-widest text-slate-400 mb-1">AMOUNT (₹)</div>
              <input
                type="number"
                min="0.01"
                step="0.01"
                value={editDiscount.amount}
                onChange={(e) => setEditDiscount({ ...editDiscount, amount: e.target.value })}
                className="w-full bg-slate-950 border border-slate-700 rounded-sm p-2 text-white font-mono-num text-lg"
                data-testid="edit-discount-amount"
              />
            </div>
            <div>
              <div className="text-xs font-mono-num tracking-widest text-slate-400 mb-1">NOTE</div>
              <input
                type="text"
                value={editDiscount.note}
                onChange={(e) => setEditDiscount({ ...editDiscount, note: e.target.value })}
                className="w-full bg-slate-950 border border-slate-700 rounded-sm p-2 text-white text-sm"
                data-testid="edit-discount-note"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setEditDiscount(null)}
                className="rounded-sm h-9 border-slate-600 text-slate-300 hover:bg-slate-800"
              >Cancel</Button>
              <Button
                onClick={async () => {
                  const amt = Number(editDiscount.amount);
                  if (!amt || amt <= 0) { toast.error("Enter a valid amount"); return; }
                  try {
                    await api.patch(`/orders/${orderId}/discounts/${editDiscount.id}`, {
                      amount: amt,
                      note: editDiscount.note,
                    });
                    toast.success("Discount updated");
                    setEditDiscount(null);
                    await refresh();
                  } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
                }}
                className="rounded-sm h-9 bg-orange-500 hover:bg-orange-600 text-white"
                data-testid="edit-discount-save"
              >Save</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>

    {/* Design uploaded — WhatsApp share modal (rendered via portal to escape Dialog's pointer-events trap) */}
    {shareModal && createPortal(
      <div
        className="fixed inset-0 z-[100] flex items-center justify-center p-4 backdrop-blur-sm"
        style={{ background: "rgba(0,0,0,0.85)", pointerEvents: "auto" }}
        onClick={(e) => { if (e.target === e.currentTarget) setShareModal(null); }}
        data-testid="whatsapp-share-modal"
      >
        <div className="bg-slate-900 border-2 border-emerald-500/50 rounded-sm max-w-md w-full p-6 shadow-2xl" style={{ pointerEvents: "auto" }}>
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-xs font-mono-num tracking-widest text-emerald-400">
                // DESIGN UPLOADED ✓
              </div>
              <div className="font-heading text-xl font-black text-white mt-1">
                Send to Customer for Approval
              </div>
            </div>
            <button
              type="button"
              onClick={() => setShareModal(null)}
              className="text-slate-400 hover:text-white p-2 -m-2 rounded hover:bg-slate-800"
              aria-label="Close"
              data-testid="modal-close-x"
            >
              <X size={22} weight="bold" />
            </button>
          </div>

          <div className="surface-2 rounded-sm p-3 space-y-1 text-xs">
            <div className="flex justify-between">
              <span className="text-slate-400 font-mono-num">Customer</span>
              <span className="text-slate-100 font-semibold">{shareModal.customer}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-400 font-mono-num">Phone</span>
              <span className="text-emerald-300 font-mono-num">+{shareModal.phone}</span>
            </div>
          </div>

          <div className="mt-4 text-xs text-slate-400 font-mono-num tracking-widest">
            // MESSAGE PREVIEW
          </div>
          <div className="mt-1 bg-slate-800/60 border border-slate-700 rounded-sm p-3 text-[11px] text-slate-300 whitespace-pre-wrap max-h-32 overflow-y-auto font-mono-num">
            {shareModal.text}
          </div>

          <a
            href={shareModal.link}
            target="_blank"
            rel="noreferrer"
            onClick={() => { setTimeout(() => setShareModal(null), 500); }}
            className="mt-5 flex items-center justify-center gap-2 w-full h-14 rounded-sm bg-emerald-600 hover:bg-emerald-500 text-white font-heading tracking-widest text-base shadow-[0_8px_24px_rgba(16,185,129,0.4)]"
            data-testid="send-whatsapp-btn"
          >
            <WhatsappLogo size={22} weight="fill" /> SEND ON WHATSAPP
          </a>

          <div className="mt-3 grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => {
                navigator.clipboard?.writeText(shareModal.text);
                toast.success("Message copied");
              }}
              className="h-10 rounded-sm border border-slate-600 text-slate-300 hover:bg-slate-800 text-xs font-mono-num tracking-widest"
              data-testid="copy-message-btn"
            >
              COPY MESSAGE
            </button>
            <button
              type="button"
              onClick={() => setShareModal(null)}
              className="h-10 rounded-sm border border-slate-600 text-slate-400 hover:bg-slate-800 text-xs font-mono-num tracking-widest"
              data-testid="modal-close-btn"
            >
              CLOSE
            </button>
          </div>

          <div className="mt-3 text-[10px] font-mono-num text-slate-500 text-center">
            Customer confirm karega → order auto: NEW → DESIGN PENDING
          </div>
        </div>
      </div>,
      document.body
    )}

    {/* Lightbox — also portal to escape any pointer-events trap */}
    {lightbox && createPortal(
      <div
        onClick={() => setLightbox(null)}
        className="fixed inset-0 bg-black/90 z-[99] flex items-center justify-center p-6 cursor-zoom-out"
      >
        <img src={lightbox.url} alt={lightbox.name} className="max-h-full max-w-full object-contain" />
      </div>,
      document.body
    )}
    </>
  );
}

function Meta({ label, value }) {
  return (
    <div className="surface p-3 rounded-sm">
      <div className="text-[10px] font-mono-num tracking-widest text-slate-500">{label.toUpperCase()}</div>
      <div className="mt-1">{value}</div>
    </div>
  );
}

function FileRow({ label, hint, icon, file, onUpload, onDownload, onView, isAdmin }) {
  const has = !!file;
  const isImage = has && (file.content_type || "").startsWith("image/");
  return (
    <div className="flex items-start justify-between gap-3 border border-slate-700 p-3 rounded-sm">
      <div className="flex items-start gap-3 flex-1 min-w-0">
        {icon}
        <div className="min-w-0">
          <div className="text-sm text-slate-200">{label}</div>
          <div className="text-[10px] font-mono-num text-slate-500 truncate">
            {has ? (file.original_filename || "uploaded") : (hint || "Not uploaded")}
          </div>
        </div>
      </div>
      <div className="flex gap-2 shrink-0">
        {has && onView && (isImage || (file.content_type || "").includes("pdf")) && (
          <Button size="sm" variant="outline" className="rounded-sm border-slate-600 h-8 text-slate-200 hover:bg-slate-800" onClick={onView}>
            <Eye size={14} className="mr-1" /> View
          </Button>
        )}
        {has && (
          <Button size="sm" variant="outline" className="rounded-sm border-slate-600 h-8 text-slate-200 hover:bg-slate-800" onClick={onDownload}>
            <DownloadSimple size={14} className="mr-1" /> Download
          </Button>
        )}
        {isAdmin && (
          <Button size="sm" onClick={onUpload} className="rounded-sm h-8 bg-slate-700 hover:bg-slate-600 text-white">
            {has ? "Replace" : "Upload"}
          </Button>
        )}
      </div>
    </div>
  );
}

function labelFor(kind) {
  return ({
    design_preview: "Design Preview",
    design_pdf: "Design PDF",
    cutting_file: "Cutting file",
    reference_image: "Reference image",
  })[kind] || kind;
}

function statusReadable(s) {
  return ({
    draft: "Draft",
    new_order: "New Order",
    design_pending: "Design in Progress",
    ready_to_cut: "Ready for Cutting",
    in_cutting: "Currently on Machine",
    completed: "Cutting Complete — Ready for Delivery",
    delivered: "Delivered & Fully Paid",
  })[s] || s;
}

/* -----------------------------------------------------------
   QuickRateCell — inline rate editor for the read-only sheets
   table inside OrderDetailDialog. Works on ALL statuses
   (including delivered/completed) because owner sometimes
   revises rates after cutting. Only rate is editable; size,
   qty, margin stay locked.
------------------------------------------------------------ */
function QuickRateCell({ orderId, itemIndex, currentRate, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(String(currentRate ?? ""));
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => { setVal(String(currentRate ?? "")); }, [currentRate]);
  useEffect(() => {
    if (editing && inputRef.current) {
      const t = setTimeout(() => { inputRef.current?.focus(); inputRef.current?.select(); }, 30);
      return () => clearTimeout(t);
    }
  }, [editing]);

  const save = async () => {
    const n = Number(val);
    if (!Number.isFinite(n) || n < 0) {
      toast.error("Rate 0 ya usse zyada valid number hona chahiye");
      return;
    }
    if (n === Number(currentRate)) {
      setEditing(false);
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.patch(`/orders/${orderId}/items/rate`, {
        item_index: itemIndex,
        rate_per_sqft: n,
      });
      onSaved && onSaved(data);
      toast.success(`Row #${itemIndex + 1} rate updated → ₹${n}`);
      setEditing(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const cancel = () => { setVal(String(currentRate ?? "")); setEditing(false); };

  if (!editing) {
    return (
      <button
        type="button"
        onClick={() => setEditing(true)}
        title="Rate change karne ke liye click karein"
        className="inline-flex items-center gap-1 text-white hover:text-orange-300 hover:bg-orange-500/10 px-1.5 py-0.5 rounded-sm transition-colors"
        data-testid={`quick-rate-edit-btn-${itemIndex}`}
      >
        ₹{currentRate}
        <PencilSimple size={11} weight="bold" className="opacity-40 group-hover:opacity-100" />
      </button>
    );
  }
  return (
    <div className="inline-flex items-center gap-1 justify-end">
      <span className="text-slate-500 text-xs">₹</span>
      <input
        ref={inputRef}
        type="number"
        step="0.01"
        min="0"
        value={val}
        disabled={busy}
        onChange={(e) => setVal(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") save();
          else if (e.key === "Escape") cancel();
        }}
        className="w-20 h-7 bg-slate-800/80 border border-orange-500/60 focus:border-orange-500 focus:outline-none text-white text-right font-mono-num rounded-sm px-1.5 text-sm no-spinner"
        data-testid={`quick-rate-input-${itemIndex}`}
      />
      <button
        type="button"
        onClick={save}
        disabled={busy}
        className="h-7 px-1.5 bg-emerald-600 hover:bg-emerald-500 text-white rounded-sm text-[10px] font-mono-num tracking-widest disabled:opacity-40"
        data-testid={`quick-rate-save-${itemIndex}`}
        title="Save (Enter)"
      >
        {busy ? "…" : "SAVE"}
      </button>
      <button
        type="button"
        onClick={cancel}
        disabled={busy}
        className="h-7 px-1.5 bg-slate-700 hover:bg-slate-600 text-slate-200 rounded-sm text-[10px] font-mono-num tracking-widest disabled:opacity-40"
        data-testid={`quick-rate-cancel-${itemIndex}`}
        title="Cancel (Esc)"
      >
        ×
      </button>
    </div>
  );
}



/* -----------------------------------------------------------
   EditableSheetsTable — used inside OrderDetailDialog when
   admin clicks "EDIT SHEETS / SIZE" to update an existing order.
   Rate, size, qty, margin & sizing-mode all editable inline.
------------------------------------------------------------ */
function EditableSheetsTable({ items, materialItems = [], updateItem, pickItem, addItem, removeItem }) {
  return (
    <div className="space-y-2" data-testid="editable-sheets-table">
      {items.map((it, idx) => {
        const isSqft = (it.unit || "sqft") === "sqft";
        const mode = it.sizing_mode || "standard";
        // Per-axis margins: prefer explicit `margin_w_inches`/`margin_h_inches`
        // (new fields), else fall back to legacy single `margin_inches`.
        const marginW = it.margin_w_inches ?? it.margin_inches ?? 3;
        const marginH = it.margin_h_inches ?? it.margin_inches ?? 3;
        return (
          <div key={idx} className="grid grid-cols-12 gap-2 items-center bg-slate-800/40 rounded-sm p-2" data-testid={`edit-sheet-row-${idx}`}>
            <div className="col-span-3">
              <Label className="text-[10px] font-mono-num text-slate-500 tracking-widest">ITEM NAME</Label>
              <ItemPicker
                value={it.item_id || ""}
                onChange={(v) => pickItem(idx, v)}
                items={orderPickerItems(materialItems, it.item_id)}
                placeholder="Select item…"
                fallbackLabel={it.item_name || ""}
                testid={`edit-item-picker-${idx}`}
              />
              {it.item_name && !it.item_id && (
                <div className="text-[10px] text-orange-400/80 mt-1 truncate" title={it.item_name}>
                  Legacy: not linked to master · pick to relink
                </div>
              )}
            </div>
            {isSqft && <>
              <div className="col-span-1">
                <Label className="text-[10px] font-mono-num text-slate-500 tracking-widest">W (in)</Label>
                <Input type="number" step="0.01" min="0" value={it.width_in}
                  onChange={(e) => updateItem(idx, "width_in", e.target.value)}
                  className="bg-slate-800/60 border-slate-600 text-white h-9 rounded-sm text-sm font-mono-num"
                  data-testid={`edit-width-${idx}`} />
              </div>
              <div className="col-span-1">
                <Label className="text-[10px] font-mono-num text-slate-500 tracking-widest">H (in)</Label>
                <Input type="number" step="0.01" min="0" value={it.height_in}
                  onChange={(e) => updateItem(idx, "height_in", e.target.value)}
                  className="bg-slate-800/60 border-slate-600 text-white h-9 rounded-sm text-sm font-mono-num"
                  data-testid={`edit-height-${idx}`} />
              </div>
            </>}
            <div className="col-span-1">
              <Label className="text-[10px] font-mono-num text-slate-500 tracking-widest">QTY</Label>
              <Input type="number" step="1" min="1" value={it.quantity}
                onChange={(e) => updateItem(idx, "quantity", e.target.value)}
                className="bg-slate-800/60 border-slate-600 text-white h-9 rounded-sm text-sm font-mono-num" />
            </div>
            <div className="col-span-1">
              <Label className="text-[10px] font-mono-num text-slate-500 tracking-widest">RATE</Label>
              <Input type="number" step="0.01" min="0" value={it.rate_per_sqft}
                onChange={(e) => updateItem(idx, "rate_per_sqft", e.target.value)}
                className="bg-slate-800/60 border-slate-600 text-white h-9 rounded-sm text-sm font-mono-num"
                data-testid={`edit-rate-${idx}`} />
            </div>
            {isSqft && (
              <>
                <div className="col-span-1">
                  <Label className="text-[10px] font-mono-num text-slate-500 tracking-widest">MODE</Label>
                  <select
                    value={mode}
                    onChange={(e) => updateItem(idx, "sizing_mode", e.target.value)}
                    className="bg-slate-800/60 border border-slate-600 text-white h-9 rounded-sm text-xs px-2 w-full"
                    data-testid={`edit-sizing-mode-${idx}`}
                  >
                    <option value="standard">MARGIN</option>
                    <option value="exact">EXACT</option>
                  </select>
                </div>
                <div className="col-span-1">
                  <Label className="text-[10px] font-mono-num text-slate-500 tracking-widest">MARG W</Label>
                  <Input
                    type="number"
                    step="0.1"
                    min="0"
                    value={marginW}
                    disabled={mode !== "standard"}
                    onChange={(e) => updateItem(idx, "margin_w_inches", e.target.value)}
                    className="bg-slate-800/60 border-slate-600 text-white h-9 rounded-sm text-sm font-mono-num disabled:opacity-40"
                    data-testid={`edit-margin-w-${idx}`}
                    placeholder="W"
                  />
                </div>
                <div className="col-span-1">
                  <Label className="text-[10px] font-mono-num text-slate-500 tracking-widest">MARG H</Label>
                  <Input
                    type="number"
                    step="0.1"
                    min="0"
                    value={marginH}
                    disabled={mode !== "standard"}
                    onChange={(e) => updateItem(idx, "margin_h_inches", e.target.value)}
                    className="bg-slate-800/60 border-slate-600 text-white h-9 rounded-sm text-sm font-mono-num disabled:opacity-40"
                    data-testid={`edit-margin-h-${idx}`}
                    placeholder="H"
                  />
                </div>
              </>
            )}
            <div className={(isSqft ? "col-span-1" : "col-span-4") + " flex items-end justify-end pt-4"}>
              <button
                type="button"
                onClick={() => removeItem(idx)}
                className="text-slate-500 hover:text-red-400 p-1"
                title="Remove sheet"
                data-testid={`edit-remove-sheet-${idx}`}
              >
                <TrashSimple size={16} />
              </button>
            </div>
          </div>
        );
      })}
      <Button
        type="button"
        onClick={addItem}
        variant="outline"
        className="w-full rounded-sm h-10 border-dashed border-slate-600 text-slate-400 hover:border-orange-500 hover:text-orange-300"
        data-testid="edit-add-sheet"
      >
        + ADD SHEET
      </Button>
      <div className="text-[10px] font-mono-num text-slate-500 mt-2">
        Note: Totals will be recomputed on save. MARG W &amp; MARG H alag alag set kar sakte ho — jaise ek 3″ aur doosra 6″. Default auto: MS=3″ · SS=6″.
      </div>
    </div>
  );
}


/* ---------- DesignNotesEditor ---------- */
/**
 * Inline-editable "DESIGN NOTES" strip. Shows the note as read-only text with
 * a small pencil icon; admin can click to edit, save calls PATCH
 * /api/orders/{id}/notes which works on ALL statuses (including delivered).
 */
function DesignNotesEditor({ order, isAdmin, onSaved }) {
  const [editing, setEditing] = React.useState(false);
  const [text, setText] = React.useState(order.design_description || "");
  const [busy, setBusy] = React.useState(false);

  React.useEffect(() => {
    setText(order.design_description || "");
    setEditing(false);
  }, [order.id, order.design_description]);

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/orders/${order.id}/notes`, {
        design_description: text.trim(),
      });
      toast.success("Design notes updated");
      setEditing(false);
      await onSaved?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Update failed");
    } finally {
      setBusy(false);
    }
  };

  const hasNote = !!(order.design_description || "").trim();

  if (!hasNote && !editing) {
    if (!isAdmin) return null;
    return (
      <div className="mt-3">
        <button
          type="button"
          onClick={() => { setEditing(true); setText(""); }}
          className="text-[11px] font-mono-num tracking-widest text-slate-500 hover:text-orange-300 transition-colors"
          data-testid="add-design-notes-btn"
        >
          + ADD DESIGN NOTES
        </button>
      </div>
    );
  }

  if (editing) {
    return (
      <div className="mt-3 rounded-sm border-l-2 border-orange-500/60 bg-slate-800/40 p-3">
        <div className="flex items-center justify-between mb-2">
          <div className="text-[10px] font-mono-num tracking-widest text-orange-400">EDIT DESIGN NOTES</div>
          <button
            type="button"
            onClick={() => { setEditing(false); setText(order.design_description || ""); }}
            className="text-slate-400 hover:text-white text-xs font-mono-num tracking-widest"
          >
            CANCEL
          </button>
        </div>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={3}
          placeholder="e.g., Slide and Swing Gate Manufacturing — 2 sides of railings + 1 gate…"
          className="w-full bg-slate-900/60 border border-slate-600 focus:border-orange-500 focus:outline-none rounded-sm p-2 text-sm text-slate-100 resize-y font-mono-num"
          data-testid="design-notes-textarea"
          autoFocus
        />
        <div className="flex items-center justify-end gap-2 mt-2">
          <Button
            onClick={save}
            disabled={busy}
            className="h-8 rounded-sm bg-orange-500 hover:bg-orange-400 text-white font-heading tracking-widest text-xs px-4"
            data-testid="design-notes-save"
          >
            {busy ? "SAVING…" : "SAVE"}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="mt-3 rounded-sm border-l-2 border-orange-500/60 bg-slate-800/40 p-3 flex items-start gap-2 text-sm group"
      data-testid="design-notes-row"
    >
      <div className="text-[10px] font-mono-num tracking-widest text-orange-400 mt-0.5 shrink-0">DESIGN NOTES</div>
      <div className="text-slate-100 whitespace-pre-wrap flex-1" data-testid="order-design-description">
        {order.design_description}
      </div>
      {isAdmin && (
        <button
          type="button"
          onClick={() => setEditing(true)}
          className="text-slate-500 hover:text-orange-300 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity"
          title="Edit design notes"
          data-testid="edit-design-notes-btn"
        >
          <PencilSimple size={14} weight="bold" />
        </button>
      )}
    </div>
  );
}

/* ---------------- Customer Bill Dialog ---------------- */
// One-off invoice at a MARKED-UP rate for the welder's end customer.
// Nothing is written to the database — the PDF is generated on-the-fly
// from the order snapshot + per-row rate overrides (or a flat markup %).
function CustomerBillDialog({ order, open, onClose }) {
  const [flatMarkup, setFlatMarkup] = React.useState("");
  const [rows, setRows] = React.useState([]);
  const [customerName, setCustomerName] = React.useState("");
  const [busy, setBusy] = React.useState(false);

  React.useEffect(() => {
    if (open && order) {
      setRows((order.items || []).map((it) => ({
        thickness: it.thickness || "",
        item_name: it.item_name || "",
        base_rate: Number(it.rate_per_sqft || 0),
        new_rate: Number(it.rate_per_sqft || 0),
      })));
      setFlatMarkup("");
      setCustomerName(order.customer_name || "");
    }
  }, [open, order]);

  const applyFlatMarkup = (pct) => {
    setFlatMarkup(pct);
    const n = Number(pct);
    if (!isNaN(n) && n !== 0) {
      setRows((prev) => prev.map((r) => ({
        ...r,
        new_rate: Math.round(r.base_rate * (1 + n / 100) * 100) / 100,
      })));
    }
  };

  const updateRowRate = (idx, v) => {
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, new_rate: Number(v) || 0 } : r)));
    setFlatMarkup(""); // manual edit — clear flat-markup indicator
  };

  const totalPreview = React.useMemo(() => {
    if (!order) return 0;
    return (order.items || []).reduce((sum, it, i) => {
      const sq = Number(it.total_sqft || 0);
      const qty = Number(it.quantity || 0);
      const unit = (it.unit || "sqft").toLowerCase();
      const rate = rows[i]?.new_rate ?? Number(it.rate_per_sqft || 0);
      return sum + (unit === "sqft" ? sq * rate : qty * rate);
    }, 0);
  }, [order, rows]);

  if (!order) return null;

  const generate = async () => {
    setBusy(true);
    try {
      const payload = {
        customer_name: customerName || undefined,
        hide_payments: true,
        hide_advance: true,
        items: rows.map((r, i) => ({ index: i, rate: r.new_rate })),
      };
      const res = await api.post(`/orders/${order.id}/custom-bill-pdf`, payload, {
        responseType: "blob",
      });
      const blobUrl = URL.createObjectURL(res.data);
      window.open(blobUrl, "_blank");
      toast.success("Customer bill generated");
      onClose();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent
        className="max-w-2xl bg-slate-950 border-slate-700 text-white rounded-sm"
        data-testid="customer-bill-dialog"
      >
        <DialogHeader>
          <div className="text-[10px] font-mono-num tracking-widest text-orange-400">// GENERATE CUSTOMER BILL</div>
          <div className="text-lg font-heading tracking-widest text-white">
            For your customer&apos;s customer
          </div>
          <div className="text-xs text-slate-500 mt-1">
            Rate change karke ek one-off bill print hoga. Order ka original price change NAHI hoga.
          </div>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          <div>
            <label className="text-[10px] font-mono-num tracking-widest text-slate-400">CUSTOMER NAME (bill par)</label>
            <input
              value={customerName}
              onChange={(e) => setCustomerName(e.target.value)}
              className="w-full bg-slate-900/60 border border-slate-600 focus:border-orange-500 focus:outline-none rounded-sm h-10 px-3 text-sm mt-1"
              data-testid="cbill-customer-name"
            />
          </div>

          <div>
            <label className="text-[10px] font-mono-num tracking-widest text-slate-400">FLAT MARKUP % (optional — quick apply)</label>
            <div className="flex items-center gap-2 mt-1">
              <input
                type="number" step="0.5" min="0"
                value={flatMarkup}
                onChange={(e) => applyFlatMarkup(e.target.value)}
                placeholder="e.g., 25 for +25%"
                className="w-40 bg-slate-900/60 border border-slate-600 focus:border-orange-500 focus:outline-none rounded-sm h-10 px-3 text-sm font-mono-num"
                data-testid="cbill-flat-markup"
              />
              <span className="text-[10px] text-slate-500 font-mono-num">
                Or edit per-row rates below
              </span>
            </div>
          </div>

          <div className="border border-slate-700 rounded-sm overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-800/50">
                <tr className="text-left text-[10px] font-mono-num text-slate-400 tracking-widest">
                  <th className="px-3 py-2">ITEM</th>
                  <th className="px-3 py-2 text-right">STORED</th>
                  <th className="px-3 py-2 text-right">CUSTOMER RATE</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i} className="border-t border-slate-800">
                    <td className="px-3 py-2">
                      <div className="text-white text-sm">{r.thickness || "—"}</div>
                      <div className="text-[10px] text-slate-500 font-mono-num">{r.item_name}</div>
                    </td>
                    <td className="px-3 py-2 text-right font-mono-num text-slate-400 text-xs">
                      ₹ {r.base_rate.toFixed(2)}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <input
                        type="number" step="0.5" min="0"
                        value={r.new_rate}
                        onChange={(e) => updateRowRate(i, e.target.value)}
                        className="w-28 bg-slate-900/60 border border-orange-500/40 focus:border-orange-500 focus:outline-none rounded-sm h-9 px-2 text-right font-mono-num text-sm"
                        data-testid={`cbill-rate-${i}`}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between bg-orange-500/10 border border-orange-500/40 rounded-sm px-4 py-3">
            <div className="text-[11px] font-mono-num text-orange-300 tracking-widest">NEW BILL TOTAL</div>
            <div className="text-2xl font-heading text-orange-300" data-testid="cbill-total">
              ₹ {totalPreview.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </div>
          </div>

          <div className="flex items-center justify-end gap-2 pt-2">
            <Button
              variant="outline"
              onClick={onClose}
              className="rounded-sm border-slate-600 text-slate-300 hover:bg-slate-800"
            >
              Cancel
            </Button>
            <Button
              onClick={generate}
              disabled={busy}
              className="rounded-sm bg-orange-500 hover:bg-orange-600 text-white font-heading tracking-widest h-10 px-5"
              data-testid="cbill-generate-btn"
            >
              <FileText size={16} className="mr-1" />
              {busy ? "GENERATING…" : "GENERATE PDF"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}



/**
 * HoldToggle — small inline widget the admin sees under the status flow.
 * When the order is `on_hold` it shows a "Resume" button; otherwise it opens
 * a prompt for the hold reason (customer's ask) before pausing the job. This
 * hides the order from the operator queue without abandoning the workflow.
 */
function HoldToggle({ order, onDone }) {
  const [busy, setBusy] = React.useState(false);
  const isHeld = order.status === "on_hold";

  const putOnHold = async () => {
    const reason = window.prompt(
      "Customer ne kya bola? (Reason for hold, e.g. 'Customer ne ruk jaane bola')",
      ""
    );
    if (reason === null) return; // cancelled
    setBusy(true);
    try {
      await api.patch(`/orders/${order.id}/hold`, { on_hold: true, reason });
      toast.success("Order put on hold — operator panel se hat gaya");
      onDone?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Hold failed");
    } finally {
      setBusy(false);
    }
  };

  const resume = async () => {
    setBusy(true);
    try {
      await api.patch(`/orders/${order.id}/hold`, { on_hold: false });
      toast.success("Order resumed — operator panel mein wapas aa gaya");
      onDone?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Resume failed");
    } finally {
      setBusy(false);
    }
  };

  if (isHeld) {
    return (
      <div className="border border-orange-500/50 bg-orange-500/10 rounded-sm p-3 mt-2" data-testid="hold-banner">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="text-orange-300 font-heading tracking-widest text-sm">ON HOLD</div>
            <div className="text-xs text-slate-400 mt-0.5">
              {order.hold_reason ? `Reason: ${order.hold_reason}` : "No reason recorded"}
              {order.held_by_name && ` · by ${order.held_by_name}`}
            </div>
          </div>
          <Button
            onClick={resume}
            disabled={busy}
            className="rounded-sm bg-emerald-600 hover:bg-emerald-500 text-white h-9"
            data-testid="resume-order-btn"
          >
            {busy ? "Resuming…" : "▶ Resume Order"}
          </Button>
        </div>
      </div>
    );
  }

  // Hold only makes sense for orders that haven't been dispatched yet.
  if (["delivered", "draft"].includes(order.status)) return null;

  return (
    <Button
      onClick={putOnHold}
      disabled={busy}
      variant="outline"
      className="w-full mt-1 rounded-sm border-orange-500/50 text-orange-300 hover:bg-orange-500/10 h-9"
      data-testid="hold-order-btn"
    >
      ⏸ Put On Hold
    </Button>
  );
}

